import cognitoApi from './cognitoAPI-module.js';
import getQueryProviderModule from './getQueryProvider-module.js';
import { refreshKioskAccessToken, reauthenticateKioskToken } from './utils/kioskTokenService.ts';

const DEFAULT_TOKEN_HEADER = 'X-Api-Token';

const TOKEN_AUTH_PROFILES = [
  {
    id: 'kiosk_token',
    headerName: DEFAULT_TOKEN_HEADER,
    refreshErrorCodes: new Set(['token_expired']),
    reauthErrorCodes: new Set([
      'INVALID_TOKEN',
      'invalid_token_signature',
      'invalid_token_version',
      'token_revoked',
      'auth_required',
      'token_required',
    ]),
    refreshToken: refreshKioskAccessToken,
    reauthenticateToken: reauthenticateKioskToken,
    onTokenUpdated: (nextToken) => {
      if (typeof getQueryProviderModule.useTokenKey === 'function') {
        getQueryProviderModule.useTokenKey('kiosk_token', nextToken);
        return;
      }
      getQueryProviderModule.useKioskToken(nextToken);
    },
  },
];

const DEFAULT_REQUEST_STATE = Object.freeze({ hasRetriedTokenAuth: false });

function OccoApi() {
  this.userMyInfoRetrieve = function (callback) {
    const body = {};
    const methodPath = '/occo-owner/user/my-info/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  // organisationId is optional
  this.robotListRetrieve = function (organisationId, callback) {
    const body = {};
    if (organisationId) {
      body.organisation_id = organisationId;
    }
    const methodPath = '/occo-owner/robot/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.stockImageListRetrieve = function (eventId, callback) {
    const body = {};
    if (eventId) {
      body.event_id = eventId;
    }
    const methodPath = '/occo-owner/stock-image/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.organisationListRetrieve = function (callback) {
    const body = {};
    const methodPath = '/occo-owner/organisation/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.createIgnoreImage = function (eventId, fullPath, callback) {
    const body = {
      event_id: eventId,
      full_path: fullPath,
    };
    const methodPath = '/occo-owner/index-ignore-faces/create';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.ignoreImageListRetrieve = function (id, callback) {
    const body = { event_id: id };
    const methodPath = '/occo-owner/index-ignore-faces/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.ignoreImageUploadAccessRetrieve = function (dir, filename, callback) {
    const body = {
      dir,
      file_name: filename,
    };
    const methodPath = '/event/index-ignore-faces-upload-access/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.deleteIgnoreImages = function (ids, eventId, callback) {
    const body = { ids, event_id: eventId };
    const methodPath = '/occo-owner/index-ignore-faces/delete';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  /*
   * key_type possible values
   *   - event_owner_api_key (keyValue type string : eventOwnerApiKey)
   *   - event_public_api_key (keyValue type string : eventPublicApiKey)
   *   - event_private_api_key (keyValue type string : eventPrivateApiKey)
   *   - attendee_uid (keyValue type string : attendeeUid)
   *   - attendee_and_org_uids (keyValue type json obj e.g. {"attendee_uid" : "value1", "organisation_uid" : "value2"})
   */

  /*
    deprecated please use eventBasicListRetrieve instead
  */
  this.eventListRetrieve = function (keyType, keyValue, callback) {
    const body = {};
    body.max_size = 5000;
    body.page_number = 1;
    const hasApiKey = keyType == null;
    if (!hasApiKey) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }
    const showApiErrors = keyType == 'event_owner_api_key';
    const methodPath = hasApiKey ? '/occo-owner/event/list/retrieve' : '/event/list/retrieve';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  this.eventBasicListRetrieve = function (callback) {
    const body = {};
    body.max_size = 5000;
    body.page_number = 1;
    const hasApiKey = true;
    const showApiErrors = true;
    const methodPath = '/occo-owner/event/basic-list/retrieve';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  this.eventFullDataRetrieve = function (eventName, callback) {
    const body = {};
    body.event_name = eventName;
    const hasApiKey = true;
    const methodPath = '/occo-owner/event/full-data/retrieve';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.eventUpdate = function (mainData, id, leadParams, callback) {
    let body = {};
    body = { ...mainData };
    body.opts = JSON.stringify(body.opts);
    body.notification_data = JSON.stringify(body.notification_data);
    body.id = id;
    body.lead_params = leadParams;
    const methodPath = '/occo-owner/event/update';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.eventChangeOrg = function (eventId, orgId, callback) {
    const body = { event_id: eventId, org_id: orgId };
    const methodPath = '/occo-owner/event/change-org';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.pptCreate = function (name, templateImageUrl, templateConfig, isPublic, isWatermarkTemplate, callback) {
    const body = {};
    body.name = name;
    body.template_image_url = templateImageUrl;
    body.template_config = templateConfig;
    body.is_public = isPublic;
    const methodPath = '/occo-owner/ppt/create';
    const hasApiKey = true;
    if (isWatermarkTemplate) {
      body.is_watermark = isWatermarkTemplate;
    }
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.pptUpdate = function (id, name, templateImageUrl, templateConfig, isPublic, isWatermarkTemplate, callback) {
    const body = {};
    body.id = id;
    body.name = name;
    body.template_image_url = templateImageUrl;
    body.template_config = templateConfig;
    body.is_public = isPublic;
    const methodPath = '/occo-owner/ppt/update';
    const hasApiKey = true;
    if (isWatermarkTemplate) {
      body.is_watermark = isWatermarkTemplate;
    }
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.pptDelete = function (id, callback) {
    const body = {};
    body.id = id;
    const methodPath = '/occo-owner/ppt/delete';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.pptRetrieve = function (id, callback) {
    const body = {};
    body.id = id;
    const methodPath = '/occo-owner/ppt/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  // organisationId is optional
  this.pptListRetrieve = function (organisationId, isWatermarkTemplate, callback) {
    const body = {};
    if (organisationId) {
      body.organisation_id = organisationId;
    }
    if (isWatermarkTemplate) {
      body.is_watermark = isWatermarkTemplate;
    }
    const methodPath = '/occo-owner/ppt/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.eventSecurePropertiesRetrieve = function (eventName, callback) {
    const body = {};
    body.event_name = eventName;
    const methodPath = '/occo-owner/event/secure-props/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.eventDelete = function (eventOwnerApiKey, eventId, callback) {
    const body = {};
    body.event_id = eventId;
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/event/delete' : '/event/delete';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.eventCreate = function (mainData, organisationId, callback) {
    let body = {};
    body = { ...mainData };
    body.opts = JSON.stringify(body.opts);
    body.notification_data = JSON.stringify(body.notification_data);
    body.organisation_id = organisationId;
    const methodPath = '/occo-owner/event/create';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  /*
   * key_type possible values
   *   - event_owner_api_key
   *   - attendee_uid
   */
  this.photoDelete = function (keyType, keyValue, photoId, callback) {
    const body = {};
    body.photo_id = photoId;
    const hasApiKey = keyType == null;
    if (!hasApiKey) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }
    const showApiErrors = keyType == 'event_owner_api_key';
    const methodPath = hasApiKey ? '/occo-owner/photo/delete' : '/photo/delete';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  /*
   * key_type possible values
   *   - event_owner_api_key
   *   - attendee_uid
   */
  this.photoListDelete = function (keyType, keyValue, photoIdArray, reason, callback) {
    const body = {};
    body.photo_id_array = photoIdArray;
    body.delete_reason = reason;
    const hasApiKey = keyType == null;
    if (!hasApiKey) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }
    const showApiErrors = keyType == 'event_owner_api_key';
    const methodPath = hasApiKey ? '/occo-owner/photo/list/delete' : '/photo/list/delete';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  this.photoTmpUploadAccessRetrieve = function (fileRelativePath, callback) {
    const body = {};
    body.file_relative_path = fileRelativePath;
    const methodPath = '/occo-owner/photo/tmp-upload-access/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.customPhotoTmpUploadAccessRetrieve = function (
    event_name,
    file_name,
    keep_origin_name,
    attendeeUid,
    callback
  ) {
    const body = {};
    body.event_name = event_name;
    body.file_name = file_name;
    body.keep_origin_name = keep_origin_name;
    body.attendee_uid = attendeeUid;
    const methodPath = '/event/tmp-photo-upload-access/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.stockImageUploadAccessRetrieve = function (dir, file_name, is_thumbnail, callback) {
    const body = {};
    body.dir = dir;
    body.file_name = file_name;
    body.is_thumbnail = is_thumbnail;
    const methodPath = '/event/stock-image-upload-access/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.createThumbnailForStockImage = function (dir, img_url, callback) {
    const body = {};
    body.dir = dir;
    body.img_url = img_url;
    const methodPath = '/event/thumbnail-for-stock-image/create';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.createCustomPhoto = function (event_name, full_path, callback) {
    const body = {};
    body.event_name = event_name;
    body.full_path = full_path;
    const methodPath = '/event/create-custom-photo';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.generateHtmlForFileSharing = function (photo_uid, event_name, callback) {
    const body = {};
    body.event_name = event_name;
    body.photo_uid = photo_uid;
    const methodPath = '/photo/generate-html-file-sharing';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.miniAlbumcreateCustomPhoto = function (event_name, full_path, attendee_uid, callback) {
    const body = {};
    body.event_name = event_name;
    body.full_path = full_path;
    body.attendee_uid = attendee_uid;
    const methodPath = '/event/create-attendee-custom-photo';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.activityCreateByRobotId = function (robotId, photoId, activityName, activityType, callback) {
    const body = {};
    body.robot_id = robotId;
    body.photo_id = photoId;
    body.activity_name = activityName;
    body.activity_type = activityType;
    const methodPath = '/activity/create-by-robot-id';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.activityBulkCreateByRobotId = function (robotId, listOfPhotoId, activityName, activityType, callback) {
    const body = {};
    body.robot_id = robotId;
    body.list_of_photo_id = listOfPhotoId;
    body.activity_name = activityName;
    body.activity_type = activityType;
    const methodPath = '/activity/bulk-create-by-robot-id';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  /*
   * key_type possible values
   *   - event_name_key (keyValue type string : eventName)
   *   - event_owner_api_key (keyValue type string : eventOwnerApiKey)
   *   - event_public_api_key (keyValue type string : eventPublicApiKey)
   *   - event_private_api_key (keyValue type string : eventPrivateApiKey)
   *   - attendee_uid (keyValue type string : attendeeUid)
   *   - attendee_and_org_uids (keyValue type json obj e.g. {"attendee_uid" : "value1", "organisation_uid" : "value2"})
   */
  this.photoListRetrieve = function (
    keyType,
    keyValue,
    eventId,
    isVisible,
    startDate,
    endDate,
    maxSize,
    pageNumber,
    minInterval,
    maxInterval,
    forKiosk,
    isCuratable,
    isCurated,
    isOriginal,
    imageProcessId,
    printable,
    printed,
    attId,
    showTotalCount,
    fileName,
    getFromTmp,
    includeFavorites,
    unattached,
    callback
  ) {
    const body = {};
    body.event_id = eventId;
    body.max_size = maxSize;
    body.page_number = pageNumber;
    body.min_time_minutes_interval = minInterval;
    body.max_time_minutes_interval = maxInterval;
    body.is_kiosk = forKiosk;
    body.is_curatable = isCuratable;
    body.is_curated = isCurated;
    body.is_original = isOriginal;
    body.image_process_id = imageProcessId;
    body.printable = printable;
    body.printed = printed;
    body.file_name = fileName;
    body.attendee_uid = attId;
    body.show_total_count = showTotalCount;
    body.get_from_tmp = getFromTmp;
    body.include_favorites = Boolean(includeFavorites);
    if (typeof isVisible !== 'undefined' && isVisible !== null && isVisible !== '' && isVisible !== 'null') {
      body.is_visible = isVisible;
    }
    if (unattached) {
      body.unattached = 1;
    }
    const hasApiKey = keyType == null;
    const isKioskToken = keyType === 'kiosk_token';
    if (startDate) {
      body.start_date = startDate;
    }
    if (endDate) {
      body.end_date = endDate;
    }
    if (!hasApiKey && !isKioskToken) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }

    const extraHeaders = isKioskToken ? { [DEFAULT_TOKEN_HEADER]: keyValue } : {};
    const showApiErrors = keyType === 'event_owner_api_key';
    const methodPath = hasApiKey ? '/occo-owner/photo/list/retrieve' : '/photo/list/retrieve';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback, extraHeaders);
  };

  this.photoListRetrieveObj = function (
    {
      keyType,
      keyValue,
      eventId,
      isVisible,
      startDate,
      endDate,
      maxSize,
      pageNumber,
      minInterval,
      maxInterval,
      forKiosk,
      isCuratable,
      isCurated,
      isOriginal,
      imageProcessId,
      printable,
      printed,
      attId,
      showTotalCount,
      fileName,
      getFromTmp,
      includeFavorites,
      unattached,
    },
    callback
  ) {
    return this.photoListRetrieve(
      keyType,
      keyValue,
      eventId,
      isVisible,
      startDate,
      endDate,
      maxSize,
      pageNumber,
      minInterval,
      maxInterval,
      forKiosk,
      isCuratable,
      isCurated,
      isOriginal,
      imageProcessId,
      printable,
      printed,
      attId,
      showTotalCount,
      fileName,
      getFromTmp,
      includeFavorites,
      unattached,
      callback
    );
  };

  this.photoFavoriteSet = function (attendeeUid, photoId, isFavorite, callback) {
    const body = {
      attendee_uid: attendeeUid,
      photo_id: photoId,
      is_favorite: isFavorite,
      event_source: 'kiosk',
    };
    const methodPath = '/attendee/photo-favorite/update';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  /*
   * This method for new printing structure, It is using print pools (aws queue)
   * key_type possible values
   *   - event_public_api_key
   *   - event_private_api_key
   *   - event_owner_api_key
   *   - attendee_uid
   */
  this.printPhotoByPrintPool = function (
    keyType,
    keyValue,
    eventId,
    photoUidArray,
    printerPoolName,
    copiesCount,
    attUid,
    callback
  ) {
    // attUid is added to pass attendee uid to ready json
    const body = {};
    body.event_id = eventId;
    body.printable_photo_uid_array = photoUidArray;
    body.printing_count = copiesCount;
    body.printer_pool_name = printerPoolName;
    body.attUid = attUid;
    const hasApiKey = keyType == null;
    if (!hasApiKey) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }
    const showApiErrors = keyType == 'event_owner_api_key' || !hasApiKey;
    const methodPath = hasApiKey ? '/occo-owner/photo/print-with-queue' : '/photo/print-with-queue';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  this.getAttendeeListByPhotoUidList = function (eventOwnerApiKey, photoUids, callback) {
    const body = {};
    body.photo_uids = photoUids;
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const showApiErrors = true;
    const methodPath = hasApiKey
      ? '/occo-owner/attendee/list/retrieve-by-photo-list'
      : '/attendee/list/retrieve-by-photo-list';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };

  /**
   * @param event_id
   * @param event_owner_api_key
   * @param  {} printable_photo_uid_array
   * @param  {} printer_pool_name
   * @param  {} attendee_uid
   * @param  {} email_to_address
   * @param  {} phone_number
   * @param  {} should_print
   * @param  {} should_unlock
   * @param  {} price
   * @param printingCount
   * @param  {} callback
   */
  this.albumDigitalPrintUnlock = function (
    event_id,
    event_owner_api_key,
    printable_photo_uid_array,
    printer_pool_name,
    attendee_uid,
    email_to_address,
    phone_number,
    should_print,
    should_unlock,
    price,
    printingCount,
    callback
  ) {
    const body = {};
    body.event_id = event_id;
    body.printable_photo_uid_array = printable_photo_uid_array;
    body.printer_pool_name = printer_pool_name;
    body.attendee_uid = attendee_uid;
    body.email_to_address = email_to_address;
    body.phone_number = phone_number;
    body.should_print = should_print;
    body.should_unlock = should_unlock;
    body.price = price;
    body.printing_count = printingCount;
    const hasApiKey = event_owner_api_key == null;
    if (!hasApiKey) {
      body.event_owner_api_key = event_owner_api_key;
    }
    const methodPath = hasApiKey
      ? '/occo-owner/attendee/unlock-and-print-by-event-owner'
      : '/attendee/unlock-and-print-by-event-owner';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.albumLock = function (event_id, event_owner_api_key, attendee_uid, callback) {
    const body = {};
    body.event_id = event_id;
    body.attendee_uid = attendee_uid;
    const hasApiKey = event_owner_api_key == null;
    if (!hasApiKey) {
      body.event_owner_api_key = event_owner_api_key;
    }
    const methodPath = hasApiKey ? '/occo-owner/attendee/lock-by-occo-owner' : '/attendee/lock-by-event-owner';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.eventMiniAlbumSendLink = function (
    emailToAddressArray,
    smsNumber,
    receiverFullName,
    senderAttendeeUid,
    photoUidArray,
    companyName,
    contactPhoneNumber,
    consentCheck,
    callback
  ) {
    const body = {};
    body.email_to_address_array = emailToAddressArray;
    body.photo_uid_array = photoUidArray;
    body.receiver_full_name = receiverFullName || null;
    body.sms_number = smsNumber || null;
    body.sender_attendee_uid = senderAttendeeUid || null;
    body.receiver_company_name = companyName || null;
    body.contact_phone_number = contactPhoneNumber || null;
    body.receiver_consent = consentCheck;

    const methodPath = '/event/mini-album/send-link';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.photoUpdate = function (eventOwnerApiKey, id, category, isVisible, callback) {
    const body = {};
    body.id = id;
    if (category) {
      body.category = category;
    }
    if (typeof isVisible !== 'undefined' || isVisible != null || isVisible != '') {
      body.is_visible = isVisible;
    }
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/photo/update' : '/photo/update';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.photoListUpdate = function (eventOwnerApiKey, idArray, category, isVisible, callback) {
    const body = {};
    body.id_array = idArray;
    if (category) {
      body.category = category;
    }
    if (typeof isVisible !== 'undefined' || isVisible != null || isVisible != '') {
      body.is_visible = isVisible;
    }
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/photo/list/update' : '/photo/list/update';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.getPhotoById = function (photoId, eventOwnerApiKey, callback) {
    const body = {};
    body.photo_id = photoId;
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/photo/retrieve' : '/photo/retrieve';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.getEventImageProcesses = function (eventId, eventOwnerApiKey, callback) {
    const body = {
      event_id: eventId,
    };
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/event/auto-print-processes' : '/event/auto-print-processes';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.getEventImageProcessesFromPhotoCuration = function (eventId, token, callback) {
    const body = {
      event_id: eventId,
      token,
    };
    occoApiPostRequest(false, '/event/auto-print-processes', body, callback);
  };

  this.updateProcessedPhoto = function (photoId, imagePath, printRequested, eventOwnerApiKey, callback) {
    const body = {};
    body.original_photo_id = photoId;
    body.full_path = imagePath;
    body.print_requested = printRequested;

    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/photo/curation-update' : '/photo/curation-update';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.updateProcessedPhotoByPhotoCuration = function (photoId, imagePath, printRequested, token, callback) {
    const body = {};
    body.original_photo_id = photoId;
    body.full_path = imagePath;
    body.print_requested = printRequested;
    body.token = token;

    occoApiPostRequest(false, '/photo/curation-update', body, callback);
  };

  this.reProcessPhoto = function (originalPhotoID, token, callback) {
    let hasApiKey = true;
    const body = {
      original_photo_id: originalPhotoID,
    };
    if (token) {
      body.token = token;
      hasApiKey = false;
    }
    const methodPath = '/photo/curation-update/reprocess';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.generateAttendeeList = function (
    { emailTo, attendeeCount, eventIds, organisationId, isPrepaid, expMinutes, pcUrlPrefixVal, partialUnlockTag },
    callback
  ) {
    const body = {
      email: emailTo,
      attendee_count: attendeeCount,
      event_id_array: eventIds,
      organisationId,
      is_prepaid: isPrepaid,
      expiration_minutes: expMinutes,
      url_prefix: pcUrlPrefixVal,
      partialUnlockTag,
    };
    const methodPath = '/occo-owner/generate-attendee-list';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.passCodeListRetrieve = function (callback) {
    const body = {};
    const methodPath = '/occo-owner/pass-code/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.passCodeDelete = function (id, callback) {
    const body = {};
    body.id = id;
    const methodPath = '/occo-owner/pass-code/delete';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.passCodeUpdate = function (
    { id, eventIdArray, organisationId, isPrepaid, expMinutes, pcUrlPrefix, partialUnlockTag },
    callback
  ) {
    const body = {};
    body.id = id;
    body.event_id_array = eventIdArray;
    body.organisationId = organisationId;
    body.is_prepaid = isPrepaid;
    body.expiration_minutes = expMinutes;
    body.url_prefix = pcUrlPrefix;
    body.partialUnlockTag = partialUnlockTag;
    const methodPath = '/occo-owner/pass-code/update';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.processingListRetrieve = function (callback) {
    const body = {};
    const methodPath = '/occo-owner/image-process/list/retrieve';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.shopifyBuyProductFromKiosk = function (
    attendee_uid,
    event_private_api_key,
    employee_secret_code,
    auth_Required,
    shopify_checkout,
    isByCash,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    body.employee_secret_code = employee_secret_code;
    body.auth_required = auth_Required;
    console.log(shopify_checkout);
    body.shopify_checkout = shopify_checkout;
    body.isByCash = isByCash;
    const methodPath = '/shopify/buy-product-from-kiosk';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.createShopifyDraftOrder = function (attendee_uid, event_private_api_key, shopify_cart_id, callback) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    body.cart_id = shopify_cart_id;
    const methodPath = '/shopify/admin/create-draft-order';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.completeShopifyDraftOrder = function (
    attendee_uid,
    event_private_api_key,
    draft_order_id,
    employee_secret_code,
    auth_required,
    is_by_cash,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    body.draft_order_id = draft_order_id;
    body.employee_secret_code = employee_secret_code;
    body.auth_required = auth_required;
    body.is_by_cash = is_by_cash;
    const methodPath = '/shopify/admin/complete-draft-order';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.completeShopifyDraftOrderSelfCheckout = function (
    attendee_uid,
    event_private_api_key,
    draft_order_id,
    gateway,
    shopifyTag,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    body.draft_order_id = draft_order_id;
    body.gateway = gateway;
    body.shopify_tag = shopifyTag;
    const methodPath = '/shopify/admin/complete-draft-order-self-checkout';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.sendReceipt = function (attendeeUid, eventPrivateApiKey, receiptImgUrl, callback) {
    const body = {};
    body.attendee_uid = attendeeUid;
    body.event_private_api_key = eventPrivateApiKey;
    body.receipt_img_url = receiptImgUrl;
    const methodPath = '/shopify/send-receipt';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.shopifyBuyProductFromKioskSelfCheckout = function (
    attendee_uid,
    event_private_api_key,
    shopify_checkout,
    gateway,
    shopify_tag,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    body.shopify_checkout = shopify_checkout;
    body.gateway = gateway;
    body.shopify_tag = shopify_tag;
    const methodPath = '/shopify/buy-product-from-kiosk-self-checkout';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.initiateSquarePaymentForKiosk = function (square_access_token, device_id, price, currency, callback) {
    const body = {};
    body.device_id = device_id;
    body.price = price;
    body.square_access_token = square_access_token;
    body.currency = currency;
    const methodPath = '/attendee/initiate-square-payment';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.checkSquareCheckoutStatus = function (square_access_token, checkout_id, callback) {
    const body = {};
    body.checkout_id = checkout_id;
    body.square_access_token = square_access_token;
    const methodPath = '/attendee/check-square-checkout-status';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.qrPaymentGenerateLink = function (params, callback) {
    const body = {
      ...params,
    };
    const methodPath = '/payment/create-payment-link';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.qrPaymentCheckStatus = function (params, callback) {
    const body = {
      ...params,
    };
    const methodPath = '/payment/check-payment-status';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.qrPaymentDisableLink = function (eventCode, paymentLinkId, provider, callback) {
    const body = {
      provider,
      eventCode,
      paymentLinkId,
    };
    const methodPath = '/payment/disable-payment-link';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.attendeeUnlockPrint = function (
    attendee_uid,
    event_private_api_key,
    employee_secret_code,
    should_print,
    should_unlock,
    email_to_address,
    phone_number,
    printer_pool_name,
    printable_photo_uid_array,
    auth_Required,
    price,
    paymentType,
    kioskId,
    partialUnlockTags,
    callback
  ) {
    const body = {
      attendee_uid,
      event_private_api_key,
      employee_secret_code,
      should_print,
      auth_required: auth_Required,
      should_unlock,
      email_to_address,
      phone_number,
      printer_pool_name,
      printable_photo_uid_array,
      price,
      payment_type: paymentType,
      kioskId,
      partialUnlockTags:
        partialUnlockTags && partialUnlockTags !== 'undefined' && partialUnlockTags !== 'false'
          ? partialUnlockTags
          : null,
      printing_count: 1,
    };
    const methodPath = '/attendee/unlock-and-print';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.attendeeUnlockPurchase = function (
    attendee_uid,
    event_code,
    email_to_address,
    phone_number,
    price,
    payment_id,
    paymentType,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_code = event_code;
    body.email_to_address = email_to_address;
    body.phone_number = phone_number;
    body.price = price;
    body.payment_id = payment_id;
    body.payment_type = paymentType;
    const methodPath = '/attendee/unlock';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.attendeeAttachPhotos = function (
    attendeeUid,
    eventId,
    photoUidArray,
    keyType,
    keyValue,
    groupAttendees,
    callback
  ) {
    const body = {};
    body.attendee_uid = attendeeUid;
    body.event_id = eventId;
    body.photo_uid_array = photoUidArray;

    const hasApiKey = keyType == null;
    if (!hasApiKey) {
      body.key_type = keyType;
      body.key_value = keyValue;
    }
    body.group_attendees = groupAttendees;

    const methodPath = hasApiKey ? '/occo-owner/attendee/attach-photos' : '/attendee/attach-photos';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.unlinkPhotos = function (eventOwnerApiKey, eventId, photo_uid_array, attendee_uid, callback) {
    const body = { eventId, photo_uid_array, attendee_uid };
    const hasApiKey = eventOwnerApiKey == null;
    if (!hasApiKey) {
      body.event_owner_api_key = eventOwnerApiKey;
    }
    const methodPath = hasApiKey ? '/occo-owner/attendee/unlink-photos' : '/attendee/unlink-photos';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.groupAccounts = function (eventPrivateApiKey, attendeeUids, callback) {
    const body = { attendee_uid_list: attendeeUids, event_private_api_key: eventPrivateApiKey };
    const methodPath = '/attendee/check-and-group-uid-list';
    occoApiPostRequest(false, methodPath, body, callback);
  };

  this.attendeeAttachPhotosByQR = function (attendee_uid, photo_uid_array, callback) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.photo_uid_array = photo_uid_array;
    const methodPath = '/attendee/attach-photos-by-qr-scan';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.getAttendee = function (attendee_uid, callback) {
    const body = {};
    body.attendee_uid = attendee_uid;
    const methodPath = '/attendee/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.getAttendeePrivateIdComp = function (attendee_uid, event_private_api_key, callback) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.event_private_api_key = event_private_api_key;
    const methodPath = '/attendee/check-uid';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.updateAttendeeEmail = function (attendee_uid, email, callback) {
    const body = {};
    body.attendee_uid = attendee_uid;
    body.email_address = email;
    const methodPath = '/attendee/update-by-kiosk';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  // profile picture methods
  this.profilePhotoListRetrieve = function (attId, callback) {
    const body = {};
    body.attendee_uid = attId;
    const methodPath = '/attendee/profile-photo/list/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.assetTmpUploadAccessRetrieve = function (fileRelativePath, hasApiKey, callback) {
    const body = {
      file_relative_path: fileRelativePath,
    };
    const methodPath = hasApiKey ? '/occo-owner/asset/tmp-upload-access/retrieve' : '/asset/tmp-upload-access/retrieve';
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.profilePhotoTmpUploadAccessRetrieve = function (file_name, callback) {
    const body = {};
    body.file_relative_path = file_name;
    const methodPath = '/photo/tmp-upload-access/retrieve';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.createProfilePhoto = function (attId, full_path, event_name, callback) {
    const body = {};
    body.attendee_uid = attId;
    body.full_path = full_path;
    body.event_name = event_name;
    const methodPath = '/attendee/profile-photo/create';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };
  this.deleteProfilePhoto = function (attId, photoId, callback) {
    const body = {};
    body.attendee_uid = attId;
    body.photo_uid = photoId;
    const methodPath = '/attendee/profile-photo/delete';
    const hasApiKey = false;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.getSquareLocations = function (squareAccessToken, callback) {
    const body = {};
    body.square_access_token = squareAccessToken;
    const methodPath = '/occo-owner/square/locations';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.createSquareDeviceCode = function (squareAccessToken, deviceName, locationId, callback) {
    const body = {};
    body.square_access_token = squareAccessToken;
    body.device_name = deviceName;
    body.location_id = locationId;
    const methodPath = '/occo-owner/square/create-device-code';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  this.getSquareDeviceCodeById = function (squareAccessToken, deviceCodeId, callback) {
    const body = {};
    body.square_access_token = squareAccessToken;
    body.device_code_id = deviceCodeId;
    const methodPath = '/occo-owner/square/get-device-code';
    const hasApiKey = true;
    occoApiPostRequest(hasApiKey, methodPath, body, callback);
  };

  /**
   * @param  {} authorization
   * @param  {} event_id
   *  @param {} event_owner_api_key
   * @param  {} photo_url_list
   * @param  {} start_date
   * @param  {} end_date
   * @param  {} callback
   */
  this.searchByFace = function (
    event_id,
    event_owner_api_key,
    photo_url_list,
    face_match_threshold,
    start_date,
    end_date,
    callback
  ) {
    const body = {};
    body.photo_url_list = photo_url_list;
    body.start_date = start_date;
    body.end_date = end_date;
    body.event_owner_api_key = event_owner_api_key;
    body.event_id = event_id;
    body.face_match_threshold = face_match_threshold;
    let hasApiKey = true;
    if (event_owner_api_key) {
      hasApiKey = false;
    }
    const showApiErrors = body.event_owner_api_key !== true;
    const methodPath = hasApiKey ? '/occo-owner/photo/search-by-face' : '/photo/search-by-face';
    occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback);
  };
}

function occoApiPostRequest(hasApiKey, methodPath, body, callback) {
  occoApiPostRequestBase(hasApiKey, methodPath, body, false, callback);
}

function buildOccoError(showErr, resp, textStatus, jqXHR) {
  const err = {};

  err.type = 'occo';
  err.message_key = showErr ? resp.error_message_key : 'Internal Error';
  err.message_text = showErr ? resp.error?.message_text : 'Internal Error';
  err.textStatus = textStatus;
  err.jqXHR = jqXHR;
  err.resp = resp;

  return err;
}

function buildInternalOccoError(resp, textStatus, jqXHR) {
  const err = {};

  err.type = 'occo';
  err.message_key = 'Internal Error';
  err.jqXHR = jqXHR;
  err.textStatus = textStatus;
  err.resp = resp;

  return err;
}

function buildGlobalError(jqXHR, textStatus, errorThrown) {
  const err = {};

  err.type = 'global';
  err.message_key = 'Internal Error';
  err.jqXHR = jqXHR;
  err.textStatus = textStatus;
  err.errorThrown = errorThrown;

  return err;
}

function getKioskAuthErrorCode(resp, jqXHR) {
  const responsePayload = resp ?? jqXHR?.responseJSON;

  return (
    responsePayload?.error_message_key ??
    responsePayload?.error?.code ??
    responsePayload?.error?.message_key ??
    responsePayload?.error_message ??
    null
  );
}

function resolveTokenAuthProfile(extraHeaders) {
  if (!extraHeaders) {
    return null;
  }

  return TOKEN_AUTH_PROFILES.find((profile) => Boolean(extraHeaders[profile.headerName])) ?? null;
}

function getTokenRecoveryAction(profile, errorCode) {
  if (profile.refreshErrorCodes.has(errorCode)) {
    return profile.refreshToken;
  }

  if (profile.reauthErrorCodes.has(errorCode)) {
    return profile.reauthenticateToken;
  }

  return null;
}

function maybeRecoverTokenAuth(resp, jqXHR, extraHeaders, hasRetriedTokenAuth, retryRequest, onRecoveryFailure) {
  if (hasRetriedTokenAuth) {
    return false;
  }

  const profile = resolveTokenAuthProfile(extraHeaders);

  if (!profile) {
    return false;
  }

  const errorCode = getKioskAuthErrorCode(resp, jqXHR);
  const recoveryAction = getTokenRecoveryAction(profile, errorCode);

  if (!recoveryAction) {
    return false;
  }

  recoveryAction(globalThis.BACKEND_API)
    .then((nextAccessToken) => {
      profile.onTokenUpdated(nextAccessToken);
      retryRequest(profile, nextAccessToken);
    })
    .catch((error) => {
      onRecoveryFailure(error);
    });

  return true;
}

function occoApiPostRequestBase(hasApiKey, methodPath, body, showApiErrors, callback, extraHeaders, requestState) {
  const normalizedExtraHeaders = extraHeaders ?? {};
  const showErr = hasApiKey || showApiErrors || globalThis.DEBUG_MODE;
  const { hasRetriedTokenAuth } = requestState ?? DEFAULT_REQUEST_STATE;

  const retryWithRecoveredToken = (profile, nextAccessToken) => {
    occoApiPostRequestBase(
      hasApiKey,
      methodPath,
      body,
      showApiErrors,
      callback,
      { ...normalizedExtraHeaders, [profile.headerName]: nextAccessToken },
      { hasRetriedTokenAuth: true }
    );
  };
  const ajaxParams = {
    url: globalThis.BACKEND_API + methodPath,
    type: 'POST',
    dataType: 'json',
    data: JSON.stringify(body),

    success(resp, textStatus, jqXHR) {
      if (resp.status === 'ok') {
        callback(null, resp.result);
      } else if (resp.status === 'nok') {
        const err1 = buildOccoError(showErr, resp, textStatus, jqXHR);

        if (
          maybeRecoverTokenAuth(
            resp,
            jqXHR,
            normalizedExtraHeaders,
            hasRetriedTokenAuth,
            retryWithRecoveredToken,
            () => {
              console.error('err', JSON.stringify(err1));
              callback(err1, null);
            }
          )
        ) {
          return;
        }

        console.error('err', JSON.stringify(err1));
        callback(err1, null);
      } else {
        const err2 = buildInternalOccoError(resp, textStatus, jqXHR);
        console.error('err', JSON.stringify(err2));
        callback(err2, null);
      }
    },
    error(jqXHR, textStatus, errorThrown) {
      if (jqXHR.readyState == 0 || jqXHR.status == 0) {
        return; // it's not really an error,
        // it's accure when user refresh page before ajax call complition
      }

      if (
        maybeRecoverTokenAuth(null, jqXHR, normalizedExtraHeaders, hasRetriedTokenAuth, retryWithRecoveredToken, () => {
          const err = buildGlobalError(jqXHR, textStatus, errorThrown);
          console.error('err', JSON.stringify(err));
          callback(err, null);
        })
      ) {
        return;
      }

      const err = buildGlobalError(jqXHR, textStatus, errorThrown);
      console.error('err', JSON.stringify(err));
      callback(err, null);
    },
  };
  if (hasApiKey) {
    cognitoApi.getApiKey((err, apiKey) => {
      if (err) {
        location.href = './';
        return;
      }
      ajaxParams.headers = {
        Authorization: apiKey,
        ...normalizedExtraHeaders,
      };
      $.ajax(ajaxParams);
    });
  } else {
    if (Object.keys(normalizedExtraHeaders).length > 0) {
      ajaxParams.headers = { ...normalizedExtraHeaders };
    }
    $.ajax(ajaxParams);
  }
}

export default new OccoApi();
